import { CreateWordModal } from '@/components/words/editor/create-word-modal';
import { wordsApi } from '@/lib/api';
import { NOYAU_LINK_COLOR, parseSeeAlsoEntries, serializeSeeAlsoEntries } from '@/lib/word-links';
import type { Word, WordSummary } from '@/schemas/word';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

interface SeeAlsoEditorProps {
  /** The Tahiti `see_also` markdown — one `- [label](#<numericId>)` line per related word (bare `- label` tolerated on read, never resolved). */
  seeAlso: string;
  /** All words, used to resolve each reference's slug + noyau colour by numeric id. */
  wordList: WordSummary[];
  /** Slug of the word being edited — excluded from suggestions (no self-reference). */
  currentSlug: string;
  readOnly: boolean;
  onChange: (next: string) => void;
  onNavigate: (wordId: string) => void;
}

const MAX_SUGGESTIONS = 8;
/** Below this length a live backend search is more noise than signal — wait for more input. */
const LIVE_SEARCH_MIN_LENGTH = 2;
/** Debounce before firing a live search-as-you-type request. */
const LIVE_SEARCH_DEBOUNCE_MS = 300;

/**
 * Editable "Voir aussi" section: a list of colour-coded internal links to other
 * dictionary words, persisted as the `see_also` markdown column. In edit mode each
 * link can be removed and new ones added via an inline autocomplete that reuses
 * {@link CreateWordModal} when the typed word does not exist yet. Colour is
 * resolved from the word list by the entry's numeric id (the entry's label is
 * display-only, resolution is never by title). In read-only mode it renders
 * plain navigable links and hides entirely when empty.
 */
export function SeeAlsoEditor({
  seeAlso,
  wordList,
  currentSlug,
  readOnly,
  onChange,
  onNavigate,
}: SeeAlsoEditorProps) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingText, setPendingText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  // Debounce the typed query before firing a live backend search — the passed-in
  // wordList is a capped default page and may not contain every word, so autocomplete
  // supplements it with a live search rather than relying on that page alone.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < LIVE_SEARCH_MIN_LENGTH) {
      setDebouncedQuery('');
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(trimmed), LIVE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: liveMatches = [] } = useQuery<WordSummary[]>({
    queryKey: ['words', 'search', debouncedQuery],
    queryFn: () => wordsApi.search(debouncedQuery).then((page) => page.items),
    enabled: debouncedQuery.length >= LIVE_SEARCH_MIN_LENGTH,
    staleTime: 30_000,
  });

  const entries = parseSeeAlsoEntries(seeAlso);
  const trimmed = query.trim();
  const taken = new Set(entries.map((e) => e.label.toLowerCase()));
  const candidateWords =
    liveMatches.length === 0
      ? wordList
      : [...new Map([...wordList, ...liveMatches].map((w) => [w.id, w])).values()];
  const suggestions = trimmed
    ? candidateWords
        .filter(
          (w) =>
            w.slug !== currentSlug &&
            !taken.has(w.title.toLowerCase()) &&
            w.title.toLowerCase().includes(trimmed.toLowerCase()),
        )
        .slice(0, MAX_SUGGESTIONS)
    : [];

  // Read-only and empty: there is nothing to show.
  if (readOnly && entries.length === 0) return null;

  /** Close the autocomplete and clear its text + highlighted-suggestion state. */
  function resetAdd() {
    setAdding(false);
    setQuery('');
    setHighlight(0);
  }

  /** Append a new "voir aussi" entry (linked by numeric id), then close the autocomplete. */
  function addEntry(word: Pick<WordSummary, 'title' | 'numericId'>) {
    onChange(serializeSeeAlsoEntries([...entries, { label: word.title, id: word.numericId }]));
    resetAdd();
  }

  /** Drop the entry at the given position from the list. */
  function removeAt(index: number) {
    onChange(serializeSeeAlsoEntries(entries.filter((_, i) => i !== index)));
  }

  /**
   * Resolve what the user typed when they press Enter:
   * with no matching word, open the create modal seeded with the text;
   * otherwise add the exact title match if there is one, else the highlighted suggestion.
   */
  function confirmAdd() {
    if (!trimmed) return;
    if (suggestions.length === 0) {
      setPendingText(trimmed);
      setCreateOpen(true);
      return;
    }
    const exact = suggestions.find((w) => w.title.toLowerCase() === trimmed.toLowerCase());
    const pick = exact ?? suggestions[highlight] ?? suggestions[0];
    if (pick) addEntry(pick);
  }

  /**
   * After a word is created from the modal: refresh the word list so the new
   * word resolves (label + colour), close the modal, and add it as a link.
   */
  function handleCreateConfirm(word: Word) {
    void queryClient.invalidateQueries({ queryKey: ['words', 'all'] });
    setCreateOpen(false);
    setPendingText('');
    addEntry(word);
  }

  return (
    <section className="words-see-also">
      <span className="words-see-also__label">Voir aussi</span>

      {entries.map((entry, i) => {
        const match = entry.id ? wordList.find((w) => w.numericId === entry.id) : undefined;
        const color = match?.dominantNoyauKey
          ? NOYAU_LINK_COLOR[match.dominantNoyauKey]
          : undefined;
        return (
          <span className="words-see-also__item" key={`${entry.label}-${i}`}>
            {i > 0 && <span className="words-see-also__sep"> · </span>}
            <button
              type="button"
              className="words-see-also__link"
              style={color ? { color } : undefined}
              disabled={!match}
              onClick={() => match && onNavigate(match.id)}
            >
              {entry.label}
            </button>
            {!readOnly && (
              <button
                type="button"
                className="words-see-also__remove"
                aria-label={`Retirer ${entry.label}`}
                onClick={() => removeAt(i)}
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            )}
          </span>
        );
      })}

      {!readOnly &&
        (adding ? (
          <span className="words-see-also__combobox">
            <input
              ref={inputRef}
              className="words-see-also__input"
              type="text"
              value={query}
              placeholder="Lier un mot…"
              aria-label="Rechercher un mot à lier"
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  confirmAdd();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  resetAdd();
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlight((h) => Math.max(h - 1, 0));
                }
              }}
            />
            {suggestions.length > 0 && (
              <ul className="words-see-also__dropdown">
                {suggestions.map((w, idx) => {
                  const color = w.dominantNoyauKey
                    ? NOYAU_LINK_COLOR[w.dominantNoyauKey]
                    : undefined;
                  return (
                    <li key={w.id}>
                      <button
                        type="button"
                        aria-label={`Lier ${w.title}`}
                        className={`words-see-also__option${idx === highlight ? ' is-active' : ''}`}
                        // mousedown (not click) so it fires before the input blur
                        onMouseDown={(e) => {
                          e.preventDefault();
                          addEntry(w);
                        }}
                      >
                        <span style={color ? { color } : undefined}>{w.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </span>
        ) : (
          <button
            type="button"
            className="words-see-also__add"
            aria-label="Ajouter un lien Voir aussi"
            onClick={() => setAdding(true)}
          >
            <span className="material-symbols-rounded">add</span>
          </button>
        ))}

      {createOpen && (
        <CreateWordModal
          selectedText={pendingText}
          onConfirm={handleCreateConfirm}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </section>
  );
}
